import { css } from "@/utils/css";
import { __ } from "@wordpress/i18n";
import { v4 as uuid } from "uuid";
import { useMemo, useState } from "@wordpress/element";
import { FormBox } from "./form-box";
import { IoCloseSharp } from "react-icons/io5";

type Props = React.HTMLProps<HTMLInputElement> & {
    label?: string;
    help?: string | React.ReactNode;
    handleFileChange?: (files?: File[]) => void;
    handleClick?: (e: React.MouseEvent) => void;
    showFiles?: boolean;
};

export function FileInput({ label, help, handleFileChange, handleClick, showFiles, className, ...props }: Props) {
    const [files, setFiles] = useState<File[]>([]);
    const uid = useMemo(() => uuid(), []);

    const _handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setFiles(files);
        handleFileChange?.(files);
    };

    const filenames =
        showFiles && files.length > 1 ? `${files.length} ${__("files chosen", "antimanual")}` : files.map((file) => file.name).join(", ");

    return (
        <FormBox htmlFor={uid} label={label} help={help} className={className}>
            <label tabIndex={0} htmlFor={uid} className={css("file-input")}>
                <div className={css("upload")}>{props.multiple ? __("Choose Files", "antimanual") : __("Choose File", "antimanual")}</div>
                <div className={css("status")}>{filenames || __("No file chosen", "antimanual")}</div>
                <input {...props} type="file" id={uid} onChange={_handleFileChange} onClick={handleClick} hidden />
            </label>
            {showFiles && files.length ? (
                <div className={css("uploaded-files")} onClick={(e) => e.preventDefault()}>
                    <ul className={css("flex flex-wrap gap-2")}>
                        {files.map((file) => (
                            <li key={file.name}>
                                <span>{file.name}</span>
                                <span
                                    className={css("remove")}
                                    onClick={() => {
                                        const filtered = files.filter((f) => f.name !== file.name);
                                        setFiles(filtered);
                                        handleFileChange?.(filtered);
                                    }}
                                >
                                    <IoCloseSharp />
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <></>
            )}
        </FormBox>
    );
}
